const STATUS_COLOR = {
  finished: '#2d7d6f', running: '#2563eb', failed: '#dc2626',
  ready: '#0C9488', waiting: '#b8860b', not_configured: '#9ca3af',
};

function impactStyle(impact) {
  const c = impact === 'hoch' ? '#dc2626' : impact === 'mittel' ? '#b8860b' : '#6b7280';
  return { color: c, backgroundColor: c + '18' };
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-black/[.06] px-3.5 py-2" style={accent ? { borderColor: accent + '40' } : undefined}>
      <div className="text-[16px] font-semibold tabular-nums leading-none" style={{ color: accent || '#111f30' }}>{value ?? '–'}</div>
      <div className="text-[10.5px] uppercase tracking-wider text-gray-400 mt-1">{label}</div>
    </div>
  );
}

// Gebrandeter Gesamtreport — geteilt von der internen Report-Seite und der öffentlichen Share-Seite.
export default function ReportView({ report, project, accent = '#0C9488', logo }) {
  return (
    <div id="report-print" className="bg-white rounded-xl border border-black/[.07] shadow-sm overflow-hidden">
      <div className="px-6 py-5 flex items-center justify-between gap-4" style={{ borderTop: `4px solid ${accent}` }}>
        <div>
          <div className="text-[11px] uppercase tracking-wider" style={{ color: accent }}>SEO- &amp; Conversion-Report</div>
          <h2 className="text-[20px] font-semibold text-[#111f30] mt-0.5">{project?.name || ''}</h2>
          {project?.domain && <div className="text-[12px] text-gray-400">{project.domain}</div>}
        </div>
        {logo
          ? <img src={logo} alt="" className="h-9 object-contain" />
          : <div className="text-[12px] font-semibold text-gray-400">konversion.digital</div>}
      </div>

      <div className="px-6 pb-6 space-y-6">
        {report.executive_summary && (
          <div className="rounded-lg p-4" style={{ backgroundColor: accent + '12', borderLeft: `3px solid ${accent}` }}>
            <div className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: accent }}>Executive Summary</div>
            <p className="text-[13.5px] text-gray-700 leading-relaxed">{report.executive_summary}</p>
          </div>
        )}

        <section>
          <h3 className="text-[13px] font-semibold text-[#0D3B50] mb-2">Disziplinen</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(report.disciplines || []).map((d) => (
              <div key={d.role} className="rounded-xl border border-black/[.07] p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-[14px] text-[#111f30]">{d.label}</span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ color: STATUS_COLOR[d.status] || '#6b7280', backgroundColor: (STATUS_COLOR[d.status] || '#6b7280') + '18' }}>
                    {d.score != null && <strong className="tabular-nums">{d.score}</strong>}{d.status}
                  </span>
                </div>
                <ul className="mt-2 space-y-0.5">
                  {(d.insights || []).map((it, i) => <li key={i} className="text-[12px] text-gray-600">· {it}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-[13px] font-semibold text-[#0D3B50] mb-2">Maßnahmen</h3>
          <div className="flex flex-wrap gap-2 mb-3">
            <Stat label="Offen" value={report.measures?.open} accent={accent} />
            <Stat label="Erledigt" value={report.measures?.done} accent={accent} />
            {(report.measures?.by_assignee || []).map((a) => <Stat key={a.assignee} label={a.assignee} value={a.count} />)}
          </div>
          {(report.measures?.by_section || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {report.measures.by_section.map((s) => (
                <span key={s.section} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{s.section}: <span className="font-medium text-gray-800">{s.count}</span></span>
              ))}
            </div>
          )}
          <div className="rounded-xl border border-black/[.07] divide-y divide-gray-100">
            {(report.measures?.top_open || []).map((t, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-2.5">
                <span className="mt-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded shrink-0 uppercase" style={impactStyle(t.impact)}>{t.impact}</span>
                <span className="flex-1 text-[12.5px] text-gray-700">{t.title}</span>
                <span className="text-[11px] text-gray-400 shrink-0">{t.assignee}</span>
              </div>
            ))}
          </div>
        </section>

        {(report.recommendations || []).length > 0 && (
          <section>
            <h3 className="text-[13px] font-semibold text-[#0D3B50] mb-2">Empfehlungen</h3>
            <ul className="rounded-xl border border-black/[.07] divide-y divide-gray-100">
              {report.recommendations.map((r, i) => (
                <li key={i} className="px-4 py-2.5 text-[12.5px] text-gray-700"><span className="text-[10.5px] text-gray-400 mr-2">{r.discipline}</span>{r.title}</li>
              ))}
            </ul>
          </section>
        )}

        <div className="text-[10.5px] text-gray-400 pt-2 border-t border-gray-100">
          Erstellt von konversion.digital · automatisch durch den Reporting-Agenten zusammengefasst.
          {report.tokens ? ` · KI-Tokens: ${Number(report.tokens).toLocaleString('de-DE')}` : ''}
        </div>
      </div>
    </div>
  );
}
